import { useQueryClient } from '@tanstack/react-query';
import { filter, tap } from 'rxjs';

import { MODELING_CONST, QUERY_KEY_CONST } from '@/constant';
import {
  useModelingListCountQuery,
  useModelingListQuery,
  useSubscriptions,
  useTypedSearchParams,
} from '@/hook';
import { createSafeContext } from '@/package';
import { useWebSocket } from '@/provider';
import type { Modeling, ModelingSearchParams } from '@/type';

import type { DeploymentContextValue } from './type';

export const DeploymentContext = createSafeContext<DeploymentContextValue>(
  {} as DeploymentContextValue,
  'DeploymentContext',
);

export const DeploymentContextProvider = ({
  children,
}: React.PropsWithChildren) => {
  const { alarmMessage$ } = useWebSocket();
  const queryClient = useQueryClient();
  const { getAll } = useTypedSearchParams<ModelingSearchParams>();
  const deployStatus = getAll(
    'modelingDeployStatus',
  ) as Modeling['deployStatus'][];
  const allDeployStatus: Modeling['deployStatus'][] = Object.values(
    MODELING_CONST.MODELING_DEPLOY_STATUS,
  );
  const isAll =
    deployStatus.length === 0 ||
    allDeployStatus.every(status => deployStatus.includes(status));

  const calcDeployStatus = isAll
    ? allDeployStatus
    : (deployStatus as Modeling['deployStatus'][]);

  const { data: modelingListData } = useModelingListQuery({
    deployStatus: calcDeployStatus,
  });
  const { data: modelingCountData } = useModelingListCountQuery({
    deployStatus: calcDeployStatus,
  });

  useSubscriptions(
    () => [
      alarmMessage$
        .pipe(
          filter(
            payload =>
              payload.alarmType === 'ModelDeployDone' ||
              payload.alarmType === 'ModelDeployFail',
          ),
          tap(() => {
            queryClient.invalidateQueries({
              queryKey: QUERY_KEY_CONST.MODELING_KEYS.getList(),
            });

            queryClient.invalidateQueries({
              queryKey: QUERY_KEY_CONST.MODELING_KEYS.getCount(),
            });
          }),
        )
        .subscribe(),
    ],
    [alarmMessage$, queryClient],
  );

  return (
    <DeploymentContext.Provider<DeploymentContextValue>
      value={{
        modelingList: modelingListData ?? [],
        count: modelingCountData ?? 0,
      }}
    >
      {children}
    </DeploymentContext.Provider>
  );
};
